import { useId } from 'react'

/**
 * A 7-day sparkline inside a stat tile. Drawn only from a series that passed the chain check (the caller decides);
 * the line has no axes on purpose: the number above it is the figure, the line is its direction.
 */
export const Sparkline: React.FC<{ values: number[]; color: string; height?: number; bars?: boolean; label?: string }> = ({ values, color, height = 40, bars, label }) => {
  const id = useId().replace(/:/g, '')
  const n = values.length
  if (n < 2) return null
  const w = 160
  const h = height
  const max = Math.max(...values)
  const min = bars ? 0 : Math.min(...values)
  const span = max - min || 1
  const x = (i: number) => (i / (n - 1)) * w
  const y = (v: number) => h - 2 - ((v - min) / span) * (h - 4)
  if (bars) {
    const bw = Math.max(2, (w / n) * 0.62)
    return (
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" role="img" aria-label={label} data-testid="sparkline">
        {values.map((v, i) => {
          const top = y(v)
          // eslint-disable-next-line react/no-array-index-key -- a fixed-length day grid, no identity beyond its position
          return <rect key={i} x={(i / n) * w + ((w / n) - bw) / 2} y={top} width={bw} height={Math.max(1, h - 2 - top)} rx={1} fill={color} opacity={i === n - 1 ? 1 : 0.55} />
        })}
      </svg>
    )
  }
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`)
  const line = `M${pts.join('L')}`
  const area = `${line}L${w},${h}L0,${h}Z`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" role="img" aria-label={label} data-testid="sparkline">
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.38" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={x(n - 1)} cy={y(values[n - 1])} r="2.5" fill={color} />
    </svg>
  )
}

export default Sparkline
